import { PersonAdd } from "@mui/icons-material";
import { Grid, IconButton, Tooltip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ChipList from "components/ChipList";
import MDTypography from "components/MDTypography";
import Searchbar from "components/Searchbar";
import { useAppContextController } from "context/AppContext";
import fetchUsers from "layouts/pages/users/actions/fetchUsers";
import { useSnackbar } from "notistack";
import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useQueryClient } from "react-query";
import { INTERVIEW_ENTITIES } from "utils/constants/entityOptions";
import fetchApplicants from "../../layouts/pages/applicants/actions/fetchApplicants";

const useStyle = makeStyles({
  editor: {
    height: 150,
  },
  box: {
    overflow: "visible",
    height: 550,
  },
});

const { INTERVIEW_TYPES, STATES, INTERVIEW_STATUS } = INTERVIEW_ENTITIES;

const ParticipantsStep = ({
  currentCustomer,
  clearErrors,
  setValue,
  control,
  getValues,
  errors,
  trigger,
  watch,
  jobSlug,
  onAddExternal,
  onShowApplicantList,
  setApplicantDetails,
}) => {
  const { company, currentLoggedUser } = useAppContextController();
  const queryClient = useQueryClient();
  const classes = useStyle();
  const { enqueueSnackbar } = useSnackbar();

  const applicants = useWatch({
    control,
    name: "applicants",
  });
  const attendees = useWatch({
    control,
    name: "attendees",
  });

  const searchColumns = [
    { title: "Last Name", field: "lastName", width: 150 },
    { title: "First Name", field: "firstName", width: 150 },
    { title: "Email", field: "email", width: 200 },
    { title: "Phone", field: "phone", width: 200 },
  ];

  const searchColumnsUsers = [
    { title: "Last Name", field: "lastName", width: 150 },
    { title: "First Name", field: "firstName", width: 150 },
    { title: "Email", field: "emailAddress", width: 350 },
  ];

  const formatSearchResults = (searchResult) => {
    const formatted = searchResult?.map((v, index) => {
      if (v.status === "Deleted") return v;
      if (v.doNotMerge === "Yes") return { ...v, mergeStatus: "ignore" };
      const result = searchResult.find(
        (val, idx) =>
          val.firstName === v.firstName &&
          val.lastName === v.lastName &&
          v._id !== val._id &&
          val.doNotMerge !== "Yes" &&
          val.status !== "Deleted"
      );
      if (result) {
        return { ...v, mergeStatus: "merge" };
      }
      return v;
    });
    return formatted;
  };

  const currentApplicants = useMemo(
    () =>
      (applicants ?? []).map((item) => ({
        title: `${item.firstName} ${item.lastName}`,
        key: item.id,
      })),
    [applicants]
  );

  const currentTeamMembers = useMemo(
    () =>
      (attendees ?? []).map((item) => ({
        title: `${item.firstName} ${item.lastName}`,
        key: item.id,
      })),
    [attendees]
  );

  const onRemoveApplicant = (title, key, index) => {
    const temporaryApplicants = [...applicants];
    const updatedApplicants = temporaryApplicants.filter((_, ind) => ind !== index);
    setValue("applicants", updatedApplicants, { shouldDirty: false });
  };

  const onRemoveTeamMember = (title, key, index) => {
    const temporaryTeamMembers = [...attendees];
    const updatedTeamMembers = temporaryTeamMembers.filter((_, ind) => ind !== index);
    setValue("attendees", updatedTeamMembers, { shouldDirty: false });
  };

  return (
    <>
      <Grid container item xs={12} rowSpacing={4}>
        <Grid item xs={12}>
          <MDTypography component="label" variant="h5" fontWeight="bold" color="info">
            Invited Applicants
          </MDTypography>
          <Grid container item flexWrap="nowrap">
            <Grid container item>
              <Searchbar
                fetch={(params) =>
                  fetchApplicants({
                    ...params,
                    availableForInterviewJobSlug: jobSlug,
                  })
                }
                fetchAll={false}
                placeholder="Add Applicants"
                columns={searchColumns}
                queryCharacterLimit={3}
                resultsLimit={15}
                setFilters={() => {}}
                filters={null}
                searchBy={["firstName", "lastName", "email", "altEmail"]}
                filterBy="_id"
                scrollLimit={200}
                onRowClick={(item) => {
                  if (
                    !getValues("applicants").find((applicant) => {
                      const applId = applicant._id || applicant.id;
                      return applId === item._id;
                    })
                  ) {
                    setValue(
                      "applicants",
                      [...getValues("applicants"), setApplicantDetails(item)],
                      {
                        shouldDirty: true,
                      }
                    );
                  } else enqueueSnackbar("Applicant is already on the list", { variant: "error" });
                }}
                formatData={formatSearchResults}
                useFormStyle
                paddingRight={0}
              />
            </Grid>
            <Grid item display="flex" alignItems="center" justifyContent="center">
              <Tooltip title="View available candidates" placement="top">
                <IconButton
                  color="warning"
                  onClick={onShowApplicantList}
                  sx={{ flexShrink: 0, padding: 1 }}
                >
                  <PersonAdd />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          <Grid container item>
            <ChipList items={currentApplicants} onRemove={onRemoveApplicant} title="" />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <MDTypography component="label" variant="h5" fontWeight="bold" color="info">
            Invited Team Members
          </MDTypography>
          <Grid container item flexWrap="nowrap">
            <Grid container item>
              <Searchbar
                fetch={fetchUsers}
                fetchAll={false}
                placeholder="Add Team Members"
                columns={searchColumnsUsers}
                queryCharacterLimit={3}
                resultsLimit={15}
                setFilters={() => {}}
                filters={{ userType: "Master;Admin" }}
                searchBy={["firstName", "lastName", "emailAddress"]}
                filterBy="_id"
                scrollLimit={200}
                onRowClick={(item) => {
                  if (
                    !getValues("attendees").find((user) => {
                      const userId = user._id || user.id;
                      return userId === item._id;
                    })
                  ) {
                    setValue("attendees", [...getValues("attendees"), item], {
                      shouldDirty: true,
                    });
                  } else enqueueSnackbar("User is already on the list", { variant: "error" });
                }}
                formatData={formatSearchResults}
                useFormStyle
                paddingRight={0}
              />
            </Grid>
            <Grid item display="flex" alignItems="center" justifyContent="center">
              <Tooltip title="Add external team members" placement="top">
                <IconButton
                  color="error"
                  onClick={onAddExternal}
                  sx={{ flexShrink: 0, padding: 1 }}
                >
                  <PersonAdd />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          <Grid container item>
            <ChipList items={currentTeamMembers} onRemove={onRemoveTeamMember} title="" />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default ParticipantsStep;
